import useSWR from "swr";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { fetcher } from "@/lib/api";
import PublicHeader from "@/components/layout/PublicHeader";
import PublicFooter from "@/components/layout/PublicFooter";
import ImpactoConcelloShowcase from "@/components/impacto/ImpactoConcelloShowcase";
import ImpactoIntegrationsHealth from "@/components/impacto/ImpactoIntegrationsHealth";
import ImpactoHero from "@/components/impacto/ImpactoHero";
import ImpactoKpiGrid from "@/components/impacto/ImpactoKpiGrid";
import ImpactoTimeline from "@/components/impacto/ImpactoTimeline";
import ImpactoIntegrations from "@/components/impacto/ImpactoIntegrations";
import ImpactoTransparency from "@/components/impacto/ImpactoTransparency";
import ImpactoEnvironment from "@/components/impacto/ImpactoEnvironment";
import ImpactoFleet from "@/components/impacto/ImpactoFleet";
import ImpactoRecogida from "@/components/impacto/ImpactoRecogida";
import ImpactoCobertura from "@/components/impacto/ImpactoCobertura";
import ImpactoCarbonMap from "@/components/impacto/ImpactoCarbonMap";
import "@/i18n/fase7";
import "@/i18n/fase8";
import "@/i18n/fase9";

export default function Impacto() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language?.startsWith("es") ? "es" : "gl";
  const { data } = useSWR("/public/impacto/summary", fetcher);

  return (
    <div className="min-h-screen bg-salnes-ivory" data-testid="page-impacto">
      <PublicHeader />
      <main>
        <ImpactoHero />

        <section
          className="mx-auto max-w-[1400px] px-6 lg:px-10 py-20 lg:py-28"
          data-testid="impacto-alive"
        >
          <SectionHeader
            kicker={t("fase7.section_alive_kicker")}
            title={t("fase7.section_alive_title")}
            sub={t("fase7.section_alive_sub")}
          />
          <ImpactoKpiGrid data={data} />
        </section>

        <section
          className="bg-white border-y hairline"
          data-testid="impacto-concello-section"
        >
          <div className="mx-auto max-w-[1400px] px-6 lg:px-10 py-20 lg:py-28">
            <SectionHeader
              kicker={lang === "es" ? "Impacto por municipio" : "Impacto por concello"}
              title={
                lang === "es"
                  ? "Cambados, Meis y Vilanova, una a una"
                  : "Cambados, Meis e Vilanova, unha a unha"
              }
              sub={
                lang === "es"
                  ? "Los mismos indicadores, desagregados por municipio. Foto real y datos reales."
                  : "Os mesmos indicadores, desagregados por concello. Foto real e datos reais."
              }
            />
            <ImpactoConcelloShowcase />
          </div>
        </section>

        <section
          className="bg-white border-y hairline"
          data-testid="impacto-timeline-section"
        >
          <div className="mx-auto max-w-[1400px] px-6 lg:px-10 py-20 lg:py-28">
            <SectionHeader
              kicker={t("fase7.timeline_kicker")}
              title={t("fase7.timeline_title")}
              sub={t("fase7.timeline_sub")}
            />
            <ImpactoTimeline items={data?.timeline || []} lang={lang} />
          </div>
        </section>

        <section
          className="mx-auto max-w-[1400px] px-6 lg:px-10 py-20 lg:py-28"
          data-testid="impacto-environment"
        >
          <SectionHeader
            kicker={t("fase7.environ_kicker")}
            title={t("fase7.environ_title")}
            sub={t("fase7.environ_sub")}
          />
          <ImpactoEnvironment kpis={data?.kpis} />
        </section>

        <section
          className="bg-white border-y hairline"
          data-testid="impacto-fleet-placeholder"
          hidden
        >
          {/* iter45 · La vista pública ya no muestra KPIs específicos de la
              flota (petición cliente: "a la gente no le preocupa qué
              vehículos funcionan, dejar esto para la parte privada").
              La sección queda `hidden` para no romper anclas antiguas. */}
        </section>

        <section
          className="mx-auto max-w-[1400px] px-6 lg:px-10 py-20 lg:py-28"
          data-testid="impacto-recogida"
        >
          <SectionHeader
            kicker={t("fase9.contedores_impacto.section_kicker")}
            title={t("fase9.contedores_impacto.section_title")}
            sub={t("fase9.contedores_impacto.section_sub")}
          />
          <ImpactoRecogida />
        </section>

        <section
          className="bg-white border-y hairline"
          data-testid="impacto-cobertura-section"
        >
          <div className="mx-auto max-w-[1400px] px-6 lg:px-10 py-20 lg:py-28">
            <SectionHeader
              kicker={t("fase9.cobertura.section_kicker")}
              title={t("fase9.cobertura.section_title")}
              sub={t("fase9.cobertura.section_sub")}
            />
            <ImpactoCobertura />
          </div>
        </section>

        <section
          className="mx-auto max-w-[1400px] px-6 lg:px-10 py-20 lg:py-28"
          data-testid="impacto-carbon-by-concello"
        >
          <SectionHeader
            kicker={
              lang === "es"
                ? "Huella de carbono · Por municipio"
                : "Pegada de carbono · Por concello"
            }
            title={
              lang === "es"
                ? "Emisiones CO₂e por concello"
                : "Emisións CO₂e por concello"
            }
            sub={
              lang === "es"
                ? "Estimación derivada de los pesajes reales multiplicados por el factor oficial de recogida RSU."
                : "Estimación derivada dos pesaxes reais multiplicados polo factor oficial de recollida RSU."
            }
          />
          <ImpactoCarbonMap />
        </section>

        <section
          className="bg-salnes-sand/30 border-y hairline"
          data-testid="impacto-transparency"
        >
          <div className="mx-auto max-w-[1400px] px-6 lg:px-10 py-20 lg:py-28">
            <SectionHeader
              kicker={t("fase7.transparency_kicker")}
              title={t("fase7.transparency_title")}
            />
            <ImpactoTransparency
              kpis={data?.kpis}
              topDatasets={data?.top_datasets_30d || []}
            />
          </div>
        </section>

        <section
          className="mx-auto max-w-[1400px] px-6 lg:px-10 py-20 lg:py-28"
          data-testid="impacto-integrations"
        >
          <SectionHeader
            kicker={t("fase7.integrations_kicker")}
            title={t("fase7.integrations_title")}
            sub={
              lang === "es"
                ? "Estado en tiempo real de las fuentes de datos que alimentan esta página. Verde = al día, ámbar = con retraso, rojo = sin conexión."
                : "Estado en tempo real das fontes de datos que alimentan esta páxina. Verde = ao día, ámbar = con atraso, vermello = sen conexión."
            }
          />
          <ImpactoIntegrationsHealth />
        </section>

        <section
          className="bg-salnes-atlantic text-white"
          data-testid="impacto-financing"
        >
          <div className="mx-auto max-w-[1200px] px-6 lg:px-10 py-24 lg:py-32">
            <div className="text-[11px] uppercase tracking-[0.24em] text-white/60">
              {t("fase7.financing_kicker")}
            </div>
            <h2 className="font-serif text-3xl lg:text-5xl mt-3">
              {t("fase7.financing_title")}
            </h2>
            <div className="mt-6 flex items-center gap-6">
              <img
                src="/brand/eu-flag.svg"
                alt="EU"
                className="h-10 w-auto"
                data-testid="impacto-eu-flag"
              />
              <img
                src="/brand/prtr.svg"
                alt="PRTR"
                className="h-10 w-auto opacity-95"
                onError={(e) => { e.currentTarget.style.display = "none"; }}
              />
              <img
                src="/brand/segittur.svg"
                alt="SEGITTUR"
                className="h-10 w-auto opacity-95"
                onError={(e) => { e.currentTarget.style.display = "none"; }}
              />
            </div>
            <p className="mt-8 max-w-3xl text-white/85 leading-relaxed">
              {t("fase7.financing_body")}
            </p>
            <div className="mt-10 flex flex-wrap items-center gap-6">
              <Link
                to="/transparencia"
                className="text-sm text-white/90 hover:text-white underline underline-offset-4 decoration-white/40"
                data-testid="impacto-link-transparency"
              >
                {t("fase7.financing_link_transparency")}
              </Link>
              <Link
                to="/documentacion"
                className="text-sm text-white/90 hover:text-white underline underline-offset-4 decoration-white/40"
                data-testid="impacto-link-docs"
              >
                {t("fase7.financing_link_docs")}
              </Link>
            </div>

            <div className="mt-12 flex flex-wrap gap-4">
              <Link
                to="/"
                className="inline-flex items-center px-6 py-3 rounded-full bg-white text-salnes-atlantic text-sm font-medium hover:bg-white/90 transition"
                data-testid="impacto-cta-platform"
              >
                {t("fase7.hero_cta_platform")}
              </Link>
              <Link
                to="/datos-abertos"
                className="inline-flex items-center px-6 py-3 rounded-full border border-white/40 text-white text-sm hover:bg-white/10 transition"
                data-testid="impacto-cta-data"
              >
                {t("fase7.hero_cta_data")}
              </Link>
            </div>
          </div>
        </section>
      </main>
      <PublicFooter />
    </div>
  );
}

function SectionHeader({ kicker, title, sub }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5 }}
      className="mb-12"
    >
      <div className="text-[11px] uppercase tracking-[0.24em] text-salnes-atlantic">
        {kicker}
      </div>
      <h2 className="font-serif text-3xl lg:text-5xl mt-3 text-salnes-ink max-w-3xl">
        {title}
      </h2>
      {sub && (
        <p className="mt-4 text-salnes-stone max-w-2xl text-base lg:text-lg">
          {sub}
        </p>
      )}
    </motion.div>
  );
}
